import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import { useAuth0 } from "@auth0/auth0-react";
import axiosInstance from "../../../../appHelpers/axiosInstance";
import { Card, Button, Loader, colors, gradients, shadows } from "../../../ui";
import { DarkModeContext } from "../../../../appContexts/DarkModeContext";
import CopyIcon from "@rsuite/icons/Copy";
import CheckIcon from "@rsuite/icons/Check";
import WarningRoundIcon from "@rsuite/icons/WarningRound";

export const _CredentialsFC = () => {
  const { darkMode } = useContext(DarkModeContext);
  const { getAccessTokenSilently } = useAuth0();

  const [credentials, setCredentials] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [showSecret, setShowSecret] = useState(false);
  const [activeTab, setActiveTab] = useState("credentials");

  useEffect(() => {
    fetchCredentials();
  }, []);

  const fetchCredentials = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const response = await axiosInstance({
        method: "GET",
        url: `/auth/credentials`,
        headers: { authorization: `Bearer ${token}` },
      });

      setCredentials(response.data);
    } catch (err) {
      console.error("Failed to fetch credentials:", err);
      setError(
        err.response?.data?.message ||
          "Failed to retrieve credentials. Please contact an administrator."
      );
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const maskSecret = (secret) => {
    if (!secret) return "";
    return secret.substring(0, 4) + "••••••••••••••••" + secret.slice(-4);
  };

  const getTokenExample = () => {
    return `curl --request POST \\
  --url https://${process.env.REACT_APP_AUTH0_DOMAIN}/oauth/token \\
  --header 'Content-Type: application/json' \\
  --data '{
    "client_id": "${credentials?.client_id || "YOUR_CLIENT_ID"}",
    "client_secret": "${credentials?.client_secret ? "YOUR_CLIENT_SECRET" : "YOUR_CLIENT_SECRET"}",
    "audience": "${process.env.REACT_APP_AUTH0_EXPRESS_API}",
    "grant_type": "client_credentials"
  }'`;
  };

  const getTokenResponseExample = () => {
    return `{
  "access_token": "eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCIs...",
  "token_type": "Bearer",
  "expires_in": 86400
}`;
  };

  const getApiCallExample = () => {
    return `curl --request GET \\
  --url ${process.env.REACT_APP_BASE_EXPRESS_URL}/m2m/your-endpoint \\
  --header 'Authorization: Bearer YOUR_ACCESS_TOKEN' \\
  --header 'Content-Type: application/json'`;
  };

  const getPythonExample = () => {
    return `import requests

# Step 1: Get Access Token
token_url = "https://${process.env.REACT_APP_AUTH0_DOMAIN}/oauth/token"
token_payload = {
    "client_id": "${credentials?.client_id || "YOUR_CLIENT_ID"}",
    "client_secret": "YOUR_CLIENT_SECRET",
    "audience": "${process.env.REACT_APP_AUTH0_EXPRESS_API}",
    "grant_type": "client_credentials"
}

token_response = requests.post(token_url, json=token_payload)
access_token = token_response.json()["access_token"]

# Step 2: Call API with Token
api_url = "${process.env.REACT_APP_BASE_EXPRESS_URL}/m2m/your-endpoint"
headers = {
    "Authorization": f"Bearer {access_token}",
    "Content-Type": "application/json"
}

response = requests.get(api_url, headers=headers)
print(response.json())`;
  };

  const getJavaScriptExample = () => {
    return `// Step 1: Get Access Token
const tokenResponse = await fetch(
  "https://${process.env.REACT_APP_AUTH0_DOMAIN}/oauth/token",
  {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      client_id: "${credentials?.client_id || "YOUR_CLIENT_ID"}",
      client_secret: "YOUR_CLIENT_SECRET",
      audience: "${process.env.REACT_APP_AUTH0_EXPRESS_API}",
      grant_type: "client_credentials"
    })
  }
);

const { access_token } = await tokenResponse.json();

// Step 2: Call API with Token
const apiResponse = await fetch(
  "${process.env.REACT_APP_BASE_EXPRESS_URL}/m2m/your-endpoint",
  {
    method: "GET",
    headers: {
      "Authorization": \`Bearer \${access_token}\`,
      "Content-Type": "application/json"
    }
  }
);

const data = await apiResponse.json();
console.log(data);`;
  };

  if (loading) {
    return (
      <PageContainer darkMode={darkMode}>
        <LoaderWrapper>
          <Loader size="lg" content="Loading credentials..." />
        </LoaderWrapper>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer darkMode={darkMode}>
        <ErrorCard darkMode={darkMode}>
          <ErrorIcon>
            <WarningRoundIcon />
          </ErrorIcon>
          <ErrorTitle>Unable to Retrieve Credentials</ErrorTitle>
          <ErrorMessage>{error}</ErrorMessage>
          <Button onClick={fetchCredentials} style={{ marginTop: "1rem" }}>
            Try Again
          </Button>
        </ErrorCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer darkMode={darkMode}>
      <ContentWrapper>
        <PageHeader>
          <PageTitle darkMode={darkMode}>API Credentials</PageTitle>
          <PageSubtitle darkMode={darkMode}>
            Use these credentials to authenticate your application with the API
          </PageSubtitle>
        </PageHeader>

        <TabContainer darkMode={darkMode}>
          <Tab
            active={activeTab === "credentials"}
            onClick={() => setActiveTab("credentials")}
            darkMode={darkMode}
          >
            Credentials
          </Tab>
          <Tab
            active={activeTab === "usage"}
            onClick={() => setActiveTab("usage")}
            darkMode={darkMode}
          >
            Usage Guide
          </Tab>
        </TabContainer>

        {activeTab === "credentials" && (
          <>
            <CredentialsCard darkMode={darkMode}>
              {credentials?.application_name && (
                <AppNameBadge>{credentials.application_name}</AppNameBadge>
              )}

              <CredentialRow>
                <CredentialLabel darkMode={darkMode}>Client ID</CredentialLabel>
                <CredentialValueWrapper>
                  <CredentialValue darkMode={darkMode}>
                    {credentials?.client_id}
                  </CredentialValue>
                  <CopyButton
                    onClick={() =>
                      copyToClipboard(credentials?.client_id, "client_id")
                    }
                    copied={copiedField === "client_id"}
                  >
                    {copiedField === "client_id" ? <CheckIcon /> : <CopyIcon />}
                  </CopyButton>
                </CredentialValueWrapper>
              </CredentialRow>

              <Divider darkMode={darkMode} />

              <CredentialRow>
                <CredentialLabel darkMode={darkMode}>
                  Client Secret
                  <SecretToggle onClick={() => setShowSecret(!showSecret)}>
                    {showSecret ? "Hide" : "Show"}
                  </SecretToggle>
                </CredentialLabel>
                <CredentialValueWrapper>
                  <CredentialValue darkMode={darkMode} isSecret>
                    {showSecret
                      ? credentials?.client_secret
                      : maskSecret(credentials?.client_secret)}
                  </CredentialValue>
                  <CopyButton
                    onClick={() =>
                      copyToClipboard(credentials?.client_secret, "client_secret")
                    }
                    copied={copiedField === "client_secret"}
                  >
                    {copiedField === "client_secret" ? <CheckIcon /> : <CopyIcon />}
                  </CopyButton>
                </CredentialValueWrapper>
              </CredentialRow>

              <WarningBox>
                <WarningRoundIcon style={{ flexShrink: 0 }} />
                <span>
                  Keep your client secret secure. Never expose it in client-side
                  code or public repositories.
                </span>
              </WarningBox>
            </CredentialsCard>

            {credentials?.application_description && (
              <DescriptionCard darkMode={darkMode}>
                <DescriptionTitle darkMode={darkMode}>
                  Application Description
                </DescriptionTitle>
                <DescriptionText darkMode={darkMode}>
                  {credentials.application_description}
                </DescriptionText>
              </DescriptionCard>
            )}

            {credentials?.allowed_routes && credentials.allowed_routes.length > 0 && (
              <AllowedRoutesCard darkMode={darkMode}>
                <AllowedRoutesTitle darkMode={darkMode}>
                  Allowed API Endpoints
                </AllowedRoutesTitle>
                <AllowedRoutesDescription darkMode={darkMode}>
                  Your application is authorized to access the following endpoints:
                </AllowedRoutesDescription>
                <RoutesList>
                  {credentials.allowed_routes.map((route, index) => {
                    const routePath = typeof route === "object" ? route.path : route;
                    const routeMethod = typeof route === "object" ? route.method : "GET";
                    const routeDescription = typeof route === "object" ? route.description : null;
                    const routeParams = typeof route === "object" ? route.parameters : [];

                    return (
                      <RouteCard key={index} darkMode={darkMode}>
                        <RouteHeader>
                          <RouteMethod method={routeMethod}>{routeMethod}</RouteMethod>
                          <RoutePath darkMode={darkMode}>/m2m{routePath}</RoutePath>
                        </RouteHeader>
                        {routeDescription && (
                          <RouteDescription darkMode={darkMode}>
                            {routeDescription}
                          </RouteDescription>
                        )}
                        {routeParams && routeParams.length > 0 && (
                          <ParametersSection>
                            <ParametersTitle darkMode={darkMode}>Parameters</ParametersTitle>
                            <ParametersTable darkMode={darkMode}>
                              <thead>
                                <tr>
                                  <th>Name</th>
                                  <th>Type</th>
                                  <th>Required</th>
                                  <th>Default</th>
                                  <th>Description</th>
                                </tr>
                              </thead>
                              <tbody>
                                {routeParams.map((param, paramIndex) => (
                                  <tr key={paramIndex}>
                                    <td>
                                      <ParamName>{param.name}</ParamName>
                                    </td>
                                    <td>
                                      <ParamType>{param.dataType}</ParamType>
                                    </td>
                                    <td>
                                      <ParamRequired required={param.required}>
                                        {param.required ? "Yes" : "No"}
                                      </ParamRequired>
                                    </td>
                                    <td>
                                      <ParamDefault darkMode={darkMode}>
                                        {param.default || "-"}
                                      </ParamDefault>
                                    </td>
                                    <td>{param.description}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </ParametersTable>
                          </ParametersSection>
                        )}
                      </RouteCard>
                    );
                  })}
                </RoutesList>
              </AllowedRoutesCard>
            )}
          </>
        )}

        {activeTab === "usage" && (
          <UsageGuideContainer>
            <GuideSection darkMode={darkMode}>
              <SectionTitle darkMode={darkMode}>
                Step 1: Obtain an Access Token
              </SectionTitle>
              <SectionDescription darkMode={darkMode}>
                Use your client credentials to request an access token from Auth0.
                This token will be used to authenticate your API requests.
              </SectionDescription>

              <CodeBlockWrapper>
                <CodeBlockHeader darkMode={darkMode}>
                  <CodeBlockTitle>cURL</CodeBlockTitle>
                  <CopyButton
                    onClick={() => copyToClipboard(getTokenExample(), "token_curl")}
                    copied={copiedField === "token_curl"}
                    small
                  >
                    {copiedField === "token_curl" ? <CheckIcon /> : <CopyIcon />}
                  </CopyButton>
                </CodeBlockHeader>
                <CodeBlock darkMode={darkMode}>{getTokenExample()}</CodeBlock>
              </CodeBlockWrapper>

              <SectionSubtitle darkMode={darkMode}>Response</SectionSubtitle>
              <CodeBlock darkMode={darkMode}>{getTokenResponseExample()}</CodeBlock>
            </GuideSection>

            <GuideSection darkMode={darkMode}>
              <SectionTitle darkMode={darkMode}>
                Step 2: Call the API
              </SectionTitle>
              <SectionDescription darkMode={darkMode}>
                Include the access token in the Authorization header of your API
                requests using the Bearer scheme.
              </SectionDescription>

              <CodeBlockWrapper>
                <CodeBlockHeader darkMode={darkMode}>
                  <CodeBlockTitle>cURL</CodeBlockTitle>
                  <CopyButton
                    onClick={() => copyToClipboard(getApiCallExample(), "api_curl")}
                    copied={copiedField === "api_curl"}
                    small
                  >
                    {copiedField === "api_curl" ? <CheckIcon /> : <CopyIcon />}
                  </CopyButton>
                </CodeBlockHeader>
                <CodeBlock darkMode={darkMode}>{getApiCallExample()}</CodeBlock>
              </CodeBlockWrapper>
            </GuideSection>

            <GuideSection darkMode={darkMode}>
              <SectionTitle darkMode={darkMode}>
                Complete Examples
              </SectionTitle>

              <CodeBlockWrapper>
                <CodeBlockHeader darkMode={darkMode}>
                  <CodeBlockTitle>Python</CodeBlockTitle>
                  <CopyButton
                    onClick={() => copyToClipboard(getPythonExample(), "python")}
                    copied={copiedField === "python"}
                    small
                  >
                    {copiedField === "python" ? <CheckIcon /> : <CopyIcon />}
                  </CopyButton>
                </CodeBlockHeader>
                <CodeBlock darkMode={darkMode}>{getPythonExample()}</CodeBlock>
              </CodeBlockWrapper>

              <CodeBlockWrapper>
                <CodeBlockHeader darkMode={darkMode}>
                  <CodeBlockTitle>JavaScript</CodeBlockTitle>
                  <CopyButton
                    onClick={() => copyToClipboard(getJavaScriptExample(), "javascript")}
                    copied={copiedField === "javascript"}
                    small
                  >
                    {copiedField === "javascript" ? <CheckIcon /> : <CopyIcon />}
                  </CopyButton>
                </CodeBlockHeader>
                <CodeBlock darkMode={darkMode}>{getJavaScriptExample()}</CodeBlock>
              </CodeBlockWrapper>
            </GuideSection>

            <GuideSection darkMode={darkMode}>
              <SectionTitle darkMode={darkMode}>
                Important Notes
              </SectionTitle>
              <NotesList darkMode={darkMode}>
                <li>Access tokens expire after the time specified in <code>expires_in</code> (typically 24 hours). Implement token refresh logic in your application.</li>
                <li>Never expose your client secret in client-side code, mobile apps, or public repositories.</li>
                <li>Store credentials securely using environment variables or a secrets manager.</li>
                <li>Use HTTPS for all API requests to ensure data is encrypted in transit.</li>
              </NotesList>
            </GuideSection>
          </UsageGuideContainer>
        )}
      </ContentWrapper>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  width: 100%;
  height: 100%;
  min-height: 100vh;
  padding: 2rem;
  box-sizing: border-box;
  background: ${(props) =>
    props.darkMode
      ? "linear-gradient(135deg, #1a1d23 0%, #2d3748 100%)"
      : "linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)"};

  @media screen and (max-width: 768px) {
    padding: 1rem;
  }
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const ContentWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  margin-bottom: 1.5rem;
  text-align: center;
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 600;
  color: ${(props) => (props.darkMode ? "#fff" : "#1a202c")};
  margin: 0 0 0.5rem 0;
`;

const PageSubtitle = styled.p`
  font-size: 1rem;
  color: ${(props) => (props.darkMode ? "#a0aec0" : "#718096")};
  margin: 0;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 0;
  margin-bottom: 1.5rem;
  background: ${(props) =>
    props.darkMode ? "rgba(0, 0, 0, 0.3)" : "rgba(44, 90, 160, 0.1)"};
  border-radius: 12px;
  padding: 4px;
  border: 1px solid ${(props) =>
    props.darkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(44, 90, 160, 0.2)"};
`;

const Tab = styled.button`
  flex: 1;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${(props) =>
    props.active
      ? props.darkMode
        ? "#3182ce"
        : "#1a56a0"
      : "transparent"};
  color: ${(props) =>
    props.active
      ? "white"
      : props.darkMode
        ? "#a0aec0"
        : "#2c5aa0"};
  box-shadow: ${(props) =>
    props.active
      ? "0 2px 8px rgba(26, 86, 160, 0.4)"
      : "none"};

  &:hover {
    background: ${(props) =>
      props.active
        ? props.darkMode
          ? "#4299e1"
          : "#1e5fad"
        : props.darkMode
          ? "rgba(255, 255, 255, 0.1)"
          : "rgba(44, 90, 160, 0.15)"};
  }
`;

const CredentialsCard = styled.div`
  background: ${(props) =>
    props.darkMode ? "rgba(45, 55, 72, 0.8)" : "rgba(255, 255, 255, 0.95)"};
  border: 1px solid
    ${(props) =>
      props.darkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"};
  border-radius: 16px;
  padding: 2rem;
  box-shadow: ${shadows.card};
  backdrop-filter: blur(10px);
`;

const AppNameBadge = styled.div`
  display: inline-block;
  background: ${gradients.primary};
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 1.5rem;
`;

const CredentialRow = styled.div`
  margin-bottom: 1rem;
`;

const CredentialLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${(props) => (props.darkMode ? "#a0aec0" : "#718096")};
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SecretToggle = styled.button`
  background: transparent;
  border: 1px solid ${colors.primary};
  color: ${colors.primary};
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.primary};
    color: white;
  }
`;

const CredentialValueWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CredentialValue = styled.div`
  flex: 1;
  background: ${(props) =>
    props.darkMode ? "rgba(0, 0, 0, 0.3)" : "rgba(0, 0, 0, 0.04)"};
  padding: 1rem;
  border-radius: 8px;
  font-family: "SF Mono", "Fira Code", "Consolas", monospace;
  font-size: 0.9rem;
  color: ${(props) => (props.darkMode ? "#e2e8f0" : "#2d3748")};
  word-break: break-all;
  ${(props) => props.isSecret && "letter-spacing: 1px;"}
`;

const CopyButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${(props) => (props.small ? "32px" : "40px")};
  height: ${(props) => (props.small ? "32px" : "40px")};
  border: none;
  border-radius: 8px;
  background: ${(props) =>
    props.copied ? colors.success : "rgba(74, 144, 226, 0.1)"};
  color: ${(props) => (props.copied ? "white" : colors.primary)};
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: ${(props) => (props.copied ? colors.success : colors.primary)};
    color: white;
  }

  svg {
    font-size: ${(props) => (props.small ? "1rem" : "1.25rem")};
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid
    ${(props) =>
      props.darkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"};
  margin: 1.5rem 0;
`;

const WarningBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-top: 1.5rem;
  padding: 1rem;
  background: rgba(237, 137, 54, 0.1);
  border: 1px solid rgba(237, 137, 54, 0.3);
  border-radius: 8px;
  color: #ed8936;
  font-size: 0.875rem;
  line-height: 1.5;

  svg {
    font-size: 1.25rem;
    margin-top: 0.1rem;
  }
`;

const ErrorCard = styled.div`
  max-width: 500px;
  margin: 4rem auto;
  text-align: center;
  background: ${(props) =>
    props.darkMode ? "rgba(45, 55, 72, 0.8)" : "rgba(255, 255, 255, 0.95)"};
  border: 1px solid
    ${(props) =>
      props.darkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"};
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: ${shadows.card};
`;

const ErrorIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(245, 101, 101, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;

  svg {
    font-size: 2rem;
    color: #f56565;
  }
`;

const ErrorTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #f56565;
  margin: 0 0 0.75rem 0;
`;

const ErrorMessage = styled.p`
  font-size: 0.95rem;
  color: #718096;
  margin: 0;
  line-height: 1.6;
`;

const DescriptionCard = styled.div`
  margin-top: 1.5rem;
  background: ${(props) =>
    props.darkMode ? "rgba(45, 55, 72, 0.6)" : "rgba(255, 255, 255, 0.8)"};
  border: 1px solid
    ${(props) =>
      props.darkMode ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)"};
  border-radius: 12px;
  padding: 1.5rem;
`;

const DescriptionTitle = styled.h3`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${(props) => (props.darkMode ? "#a0aec0" : "#718096")};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 0.75rem 0;
`;

const DescriptionText = styled.p`
  font-size: 0.95rem;
  color: ${(props) => (props.darkMode ? "#e2e8f0" : "#4a5568")};
  margin: 0;
  line-height: 1.6;
`;

const UsageGuideContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const GuideSection = styled.div`
  background: ${(props) =>
    props.darkMode ? "rgba(45, 55, 72, 0.8)" : "rgba(255, 255, 255, 0.95)"};
  border: 1px solid
    ${(props) =>
      props.darkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"};
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: ${shadows.card};
`;

const SectionTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${(props) => (props.darkMode ? "#fff" : "#1a202c")};
  margin: 0 0 0.75rem 0;
`;

const SectionSubtitle = styled.h4`
  font-size: 0.9rem;
  font-weight: 500;
  color: ${(props) => (props.darkMode ? "#a0aec0" : "#718096")};
  margin: 1rem 0 0.5rem 0;
`;

const SectionDescription = styled.p`
  font-size: 0.95rem;
  color: ${(props) => (props.darkMode ? "#a0aec0" : "#718096")};
  margin: 0 0 1rem 0;
  line-height: 1.6;
`;

const CodeBlockWrapper = styled.div`
  margin-bottom: 1rem;
`;

const CodeBlockHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${(props) =>
    props.darkMode ? "rgba(0, 0, 0, 0.4)" : "rgba(0, 0, 0, 0.08)"};
  padding: 0.5rem 1rem;
  border-radius: 8px 8px 0 0;
  border: 1px solid
    ${(props) =>
      props.darkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"};
  border-bottom: none;
`;

const CodeBlockTitle = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  color: ${colors.primary};
`;

const CodeBlock = styled.pre`
  background: ${(props) =>
    props.darkMode ? "rgba(0, 0, 0, 0.3)" : "rgba(0, 0, 0, 0.04)"};
  padding: 1rem;
  border-radius: ${(props) =>
    props.children?.includes?.("access_token") ? "8px" : "0 0 8px 8px"};
  font-family: "SF Mono", "Fira Code", "Consolas", monospace;
  font-size: 0.85rem;
  color: ${(props) => (props.darkMode ? "#e2e8f0" : "#2d3748")};
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
  border: 1px solid
    ${(props) =>
      props.darkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"};
  line-height: 1.5;
`;

const NotesList = styled.ul`
  margin: 0;
  padding-left: 1.25rem;
  color: ${(props) => (props.darkMode ? "#a0aec0" : "#718096")};
  font-size: 0.95rem;
  line-height: 1.8;

  li {
    margin-bottom: 0.5rem;
  }

  code {
    background: ${(props) =>
      props.darkMode ? "rgba(0, 0, 0, 0.3)" : "rgba(0, 0, 0, 0.06)"};
    padding: 0.15rem 0.4rem;
    border-radius: 4px;
    font-family: "SF Mono", "Fira Code", "Consolas", monospace;
    font-size: 0.85rem;
  }
`;

const AllowedRoutesCard = styled.div`
  margin-top: 1.5rem;
  background: ${(props) =>
    props.darkMode ? "rgba(45, 55, 72, 0.8)" : "rgba(255, 255, 255, 0.95)"};
  border: 1px solid
    ${(props) =>
      props.darkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"};
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: ${shadows.card};
`;

const AllowedRoutesTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: ${(props) => (props.darkMode ? "#fff" : "#1a202c")};
  margin: 0 0 0.5rem 0;
`;

const AllowedRoutesDescription = styled.p`
  font-size: 0.9rem;
  color: ${(props) => (props.darkMode ? "#a0aec0" : "#718096")};
  margin: 0 0 1rem 0;
`;

const RoutesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const RouteCard = styled.div`
  background: ${(props) =>
    props.darkMode ? "rgba(0, 0, 0, 0.2)" : "rgba(0, 0, 0, 0.03)"};
  border-radius: 12px;
  border: 1px solid
    ${(props) =>
      props.darkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)"};
  overflow: hidden;
`;

const RouteHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border-bottom: 1px solid
    ${(props) =>
      props.darkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)"};
`;

const RouteMethod = styled.span`
  background: ${(props) => {
    switch (props.method) {
      case "GET":
        return "linear-gradient(135deg, #48bb78 0%, #38a169 100%)";
      case "POST":
        return "linear-gradient(135deg, #4299e1 0%, #3182ce 100%)";
      case "PUT":
        return "linear-gradient(135deg, #ed8936 0%, #dd6b20 100%)";
      case "DELETE":
        return "linear-gradient(135deg, #f56565 0%, #e53e3e 100%)";
      default:
        return "linear-gradient(135deg, #a0aec0 0%, #718096 100%)";
    }
  }};
  color: white;
  padding: 0.25rem 0.6rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  font-family: "SF Mono", "Fira Code", "Consolas", monospace;
  min-width: 50px;
  text-align: center;
`;

const RoutePath = styled.span`
  font-family: "SF Mono", "Fira Code", "Consolas", monospace;
  font-size: 0.95rem;
  font-weight: 500;
  color: ${(props) => (props.darkMode ? "#e2e8f0" : "#2d3748")};
`;

const RouteDescription = styled.p`
  margin: 0;
  padding: 0.75rem 1rem;
  font-size: 0.9rem;
  color: ${(props) => (props.darkMode ? "#a0aec0" : "#718096")};
  border-bottom: 1px solid
    ${(props) =>
      props.darkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)"};
`;

const ParametersSection = styled.div`
  padding: 1rem;
`;

const ParametersTitle = styled.h4`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${(props) => (props.darkMode ? "#a0aec0" : "#718096")};
  margin: 0 0 0.75rem 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ParametersTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;

  th {
    text-align: left;
    padding: 0.5rem 0.75rem;
    font-weight: 600;
    color: ${(props) => (props.darkMode ? "#a0aec0" : "#718096")};
    border-bottom: 1px solid
      ${(props) =>
        props.darkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"};
  }

  td {
    padding: 0.6rem 0.75rem;
    color: ${(props) => (props.darkMode ? "#e2e8f0" : "#4a5568")};
    border-bottom: 1px solid
      ${(props) =>
        props.darkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.04)"};
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

const ParamName = styled.code`
  background: rgba(74, 144, 226, 0.1);
  color: ${colors.primary};
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  font-family: "SF Mono", "Fira Code", "Consolas", monospace;
  font-size: 0.85rem;
  font-weight: 500;
`;

const ParamType = styled.span`
  background: rgba(128, 90, 213, 0.1);
  color: #805ad5;
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  font-family: "SF Mono", "Fira Code", "Consolas", monospace;
  font-size: 0.8rem;
`;

const ParamRequired = styled.span`
  color: ${(props) => (props.required ? "#e53e3e" : "#48bb78")};
  font-weight: 500;
`;

const ParamDefault = styled.code`
  font-family: "SF Mono", "Fira Code", "Consolas", monospace;
  font-size: 0.85rem;
  color: ${(props) => (props.darkMode ? "#a0aec0" : "#718096")};
`;
